const config = {
	DECIMAL_SEPARATOR: '.',
	KILO_SIGN: 'k',
	MEGA_SIGN: 'm',
	MINUS_SIGN: '\u2212',
	PLUS_MINUS: '+/-',
	PLUS_MINUS_SIGN: '±',
	SUFFIX_SEPARATOR: '\u200a',
	THOUSAND_SEPARATOR: '\u200a',
	TRAILING_ZEROS: false,
	USE_MULTIPLIERS: false,
	UNDEFINED: '\u2013'
};

const suffixes = {
	3: 'k',
	6: 'm',
	9: 'G',
	12: 'T',
	15: 'P'
};

const DEFAULT_PRECISION = 2;
const MAX_PRECISION = String(Number.MAX_SAFE_INTEGER || Math.pow(10, 15)).length; //NOSONAR

const pad0 = function(location, numString, length) {
	if (typeof numString !== 'string') { numString = String(numString); }

	if (location === pad0.LEFT) {
		while (numString.length < length) { numString = `0${numString}`; }
	} else if (location === pad0.RIGHT) {
		while (numString.length < length) { numString += '0'; }
	} else {
		// NOOP
	}

	return numString;
};

pad0.RIGHT = 'right';
pad0.LEFT = 'left';

const NumberUtil = {
	FIX_ZERO: 'fixed',
	LEAD_ZERO: 'leading',
	PREFIX_NEGATIVE: 'negative',
	PREFIX_POSITIVE: 'positive',
	PREFIX_ZERO: 'zero',
	TRAIL_ZERO: 'trailing'
};

const Factory = function() {
	if (this.constructor !== Factory) { return new Factory(); }

	// Getter / setter variables
	let _partition = true;
	let _precision = function() { return DEFAULT_PRECISION; };
	let _suffix = false;
	let _threshold = Infinity;

	const _prefix = { negative: true, positive: false, zero: false };
	const _zero = { leading: true, trailing: false, fixed: false };

	const format = function(value) {
		if (isNaN(value) || value === null) { return config.UNDEFINED; }

		// Check very small or very large numbers (including scientific notation)
		if (Math.abs(value) < 0.000001) {
			value = 0;
		}

		// Split the value in an integer and a decimal part
		const parts = String(value).split('.');
		const original = parseFloat(value);

		let precision = _precision(value); //NOSONAR
		let integer, decimal;

		const decimalString = (parts[1] || '').substr(0, precision);
		const paddedDecimalString = pad0(pad0.RIGHT, decimalString, precision);

		// With these we calculate our desired numbers
		integer = parseInt(parts[0], 10);
		decimal = parseInt(paddedDecimalString || 0, 10);

		const prefix = function() {
			const isZero = integer === 0 && decimal === 0;
			if (_prefix.positive && original > 0 && !isZero) { return '+'; }
			if (_prefix.zero && original === 0 && isZero) { return config.PLUS_MINUS_SIGN; }
			if (_prefix.negative && original < 0 && !isZero) { return config.MINUS_SIGN; }
			return '';
		};

		const suffix = function(numString) {
			if (_threshold === Infinity) { return numString; }

			const absolute = Math.abs(integer);
			const threshold = Math.pow(10, _threshold - 1);

			if (absolute < threshold) { return numString; }

			for (let i = 15; i >= 3; i -= 3) {
				let fraction = absolute / Math.pow(10, i);

				if (fraction >= threshold) {
					if (integer < 0) { fraction = -fraction; }
					return Factory.formatSuffix(fraction) + config.SUFFIX_SEPARATOR + (_suffix ? suffixes[i] : '');
				}
			}

			return numString;
		};

		const round = function() {
			// The rounding digit is the digit at the precision position of the decimal part
			const roundingDigit = parts[1] ? parts[1].substr(precision, 1) : 0;

			if (roundingDigit > 4) {
				decimal += 1;

				// Increment the integer part if the decimal part exceeds its number space
				if (decimal >= Math.pow(10, precision)) {
					integer += original < 0 ? -1 : 1;
					decimal = 0;
					precision = _precision(integer); //NOSONAR // update to reflect incremented integer part of value
				}
			}

			return this;
		};

		const partition = function(int) {
			// Source: https://stackoverflow.com/questions/2901102
			return int.replace(/\B(?=(\d{3})+(?!\d))/g, config.THOUSAND_SEPARATOR);
		};

		round();

		// Build the decimal part
		let decimalPart = pad0(pad0.LEFT, decimal, precision);

		// Remove trailing decimal zeros if applicable
		if (_zero.trailing === false || precision === 0) {
			let cutoff = precision;

			while (decimalPart.substr(-1, 1) === '0' && cutoff > -1) {
				cutoff -= 1;
				decimalPart = decimalPart.substr(0, cutoff);
				decimal = parseInt(decimalPart || 0, 10);
			}
		}

		if (decimalPart) { decimalPart = config.DECIMAL_SEPARATOR + decimalPart; }

		// Build the integer part
		// Remove the minus sign, it will be added again later by the prefix() method
		let integerPart = String(Math.abs(integer));

		if (_partition) { integerPart = partition(integerPart); }

		if (!_zero.leading && (decimal > 0 || _zero.trailing)) {
			integerPart = integerPart.replace(/^0$/, '');
		}

		if (integer === 0 && decimal === 0 && _zero.fixed) { decimalPart = ''; }

		return prefix() + suffix(integerPart + decimalPart);
	};

	/**
	 * Call this method at last to get the desired format function.
	 */
	this.create = function() {
		return format;
	};

	/**
	 * Getter/setter for the amount of digits displayed after the decimal point.
	 * @param {Number|Function} precision
	 */
	this.precision = function(precision) {
		if (!arguments.length) { return _precision(); }

		if (!precision && precision !== 0) { precision = DEFAULT_PRECISION; }

		if (!isNaN(precision)) { precision = Math.abs(parseInt(precision, 10)); }

		_precision = function(value) {
			return precision.apply ? precision(value) : precision;
		};

		return this;
	};

	/**
	 * Getter/setter specifying whether thousands should be grouped.
	 * @param {Boolean} partition
	 */
	this.partition = function(partition) {
		if (!arguments.length) { return _partition; }
		_partition = !!partition;
		return this;
	};

	/**
	 * Getter/setter for the sets of values that should be prefixed.
	 * Possible values (in any combination):
	 * NumberUtil.PREFIX_NEGATIVE - prefix negative values with minus sign (default)
	 * NumberUtil.PREFIX_POSITIVE - prefix positive values with plus sign
	 * NumberUtil.PREFIX_ZERO - prefix zero values with plus-minus sign
	 * @param {...String} configuration
	 */
	this.prefix = function() {
		if (!arguments.length) { return _prefix; }

		for (const key in _prefix) {
			_prefix[key] = Array.prototype.indexOf.call(arguments, key) > -1;
		}

		return this;
	};

	/**
	 * Getter/setter specifying whether suffixes should be used for large numbers.
	 * E.g. 1234567 becomes "1.234k"
	 * @param {Boolean} suffix
	 * @see NumberUtil#threshold
	 */
	this.suffix = function(suffix) {
		if (!arguments.length) { return _suffix; }
		_suffix = suffix === undefined ? true : !!suffix;
		return this;
	};

	/**
	 * Getter/setter for the amount of digits displayed before the decimal point when suffixes are used for large numbers.
	 * @param {Number} threshold
	 * @see NumberUtil#suffix
	 */
	this.threshold = function(threshold) {
		if (!arguments.length) { return _threshold; }
		_threshold = parseInt(threshold, 10);
		return this;
	};

	/**
	 * Getter/setter for handling zeroes in a formatted number string.
	 * Possible values (multiple allowed):
	 * NumberUtil.LEAD_ZERO - display leading zeroes (e.g. "0.12" vs ".12")
	 * NumberUtil.TRAIL_ZERO - display trailing zeroes (e.g. "1.20" vs "1.2")
	 * NumberUtil.FIX_ZERO - always display zero values as "0" (vs e.g. "0.00")
	 * @param {...String} configuration
	 */
	this.zero = function() {
		if (!arguments.length) { return _zero; }

		for (const key in _zero) {
			_zero[key] = Array.prototype.indexOf.call(arguments, key) > -1;
		}

		return this;
	};

	return this;
};

/**
 * This method is used for formatting the floating value of large numbers with suffixes.
 */
Factory.formatSuffix = new Factory().precision(1).create();

NumberUtil.Formatter = Factory;

/**
 * Convenience method for formatting floats in the weblyzard dashboard.
 * @param {Number} value
 * @param {Number} precision
 * @param {Boolean} alwaysShowSign
 */
NumberUtil.format = function(value, precision, alwaysShowSign) {
	const format = new Factory()
		.precision(precision)
		.prefix(NumberUtil.PREFIX_NEGATIVE, alwaysShowSign && NumberUtil.PREFIX_POSITIVE)
		.create();

	return format(value);
};

/**
 * Convenience method for formatting integers in the weblyzard dashboard.
 * @param {Number} value
 * @param {Number} alwaysShowSign
 */
NumberUtil.format.integer = function(value, alwaysShowSign) {
	const format = new Factory()
		.partition(true)
		.precision(0)
		.prefix(NumberUtil.PREFIX_NEGATIVE, alwaysShowSign && NumberUtil.PREFIX_POSITIVE)
		.create();

	return format(value);
};

/**
 * Convenience method for formatting ticks (as in charts) in the weblyzard dashboard.
 * @param {Number} value
 * @param {Number} precision
 * @param {Boolean} alwaysShowSign
 */
NumberUtil.format.tick = function(value, precision, alwaysShowSign) {
	const format = new Factory()
		.precision(precision)
		.prefix(NumberUtil.PREFIX_NEGATIVE, alwaysShowSign && NumberUtil.PREFIX_POSITIVE)
		.threshold(1)
		.suffix(true)
		.zero(false)
		.create();

	return format(value);
};

/**
 * Convenience method for formatting large numbers with suffixes in the weblyzard dashboard.
 * @param {Number} value
 * @param {Number} precision
 * @param {Boolean} showSuffix
 */
NumberUtil.format.suffix = function(value, precision, showSuffix) {
	const format = new Factory()
		.precision(precision)
		.threshold(3)
		.suffix(showSuffix)
		.create();

	return format(value);
};

/**
 * Utility method for overwriting the default configuration of the utility.
 * @param {String} name
 * @param {*} value
 */
NumberUtil.config = function(name, value) {
	if (!arguments.length) { return config; }
	if (name in config) { config[name] = value; }
	return NumberUtil;
};

/**
 * Utility method for determining the suffix (e.g. "k" or "G") of a large number.
 * @param {Number} value
 * @param {Number} threshold
 */
NumberUtil.suffix = function(value, threshold) {
	if (isNaN(threshold)) { threshold = Infinity; }
	const powerOf10 = Math.pow(10, threshold - 1);
	if (value < powerOf10) { return ''; }
	value = value / powerOf10;
	const exponent = parseInt(Math.log10(value) / 3, 10) * 3;
	return suffixes[exponent] || '';
};

export default NumberUtil;
export { NumberUtil };
